import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, DownloadSimple, ClipboardText, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { buildPendingWorksPdf } from "@/lib/pendingWorksPdf";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDeadline = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()} ${hh}:${mi}`;
};

export default function PublicPendingWorks() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/pending-works/share/${id}?token=${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setData)
      .catch(() => setError("This statement link is invalid or has expired."));
  }, [id, token]);

  const downloadPdf = () => {
    if (!data) return;
    const pdf = buildPendingWorksPdf({
      company: data.company || {},
      store: data.store || {},
      tab: data.status || "pending",
      rows: data.rows || [],
    });
    const stamp = new Date().toISOString().slice(0, 10);
    pdf.save(`pending-works-${data.status}-${stamp}.pdf`);
    toast.success("PDF downloaded");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(
      () => toast.success("Link copied"),
      () => toast.error("Copy failed — long-press the URL bar"),
    );
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground p-6 text-center">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm">Loading…</div>;

  const company = data.company || {};
  const store = data.store || {};
  const rows = data.rows || [];
  const totalAmt = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const storeName = store?.name ? ` · ${store.name}` : "";
  const tabTitle = (data.status || "pending").charAt(0).toUpperCase() + (data.status || "pending").slice(1);

  return (
    <div className="min-h-screen bg-slate-50 py-6 print:bg-white print:py-0">
      <div className="max-w-4xl mx-auto bg-white shadow-sm border border-border rounded-lg print:shadow-none print:border-0">
        {/* Sticky action bar — hidden in print */}
        <div className="flex flex-wrap gap-2 items-center justify-between px-6 py-3 border-b border-border no-print">
          <div className="text-[13px] text-muted-foreground">
            Shared statement · Read-only preview
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="pw-share-print">
              <Printer size={14} className="mr-1.5" /> Print
            </Button>
            <Button size="sm" onClick={downloadPdf} data-testid="pw-share-download">
              <DownloadSimple size={14} className="mr-1.5" /> Download PDF
            </Button>
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="pw-share-copy">
              <ClipboardText size={14} className="mr-1.5" /> Copy link
            </Button>
          </div>
        </div>

        {/* Statement body */}
        <div className="px-8 py-8">
          <div className="border-b-2 border-black pb-4 mb-6">
            <div className="text-[11px] tracking-[0.15em] uppercase text-slate-500">Daily Pending Works</div>
            <h1 className="font-heading text-2xl font-bold mt-1">{company.name || "Company"}{storeName}</h1>
            {company.address && <div className="text-[12.5px] text-slate-600 mt-1">{company.address}</div>}
            {company.gstin && <div className="text-[11.5px] text-slate-600 font-mono mt-0.5">GSTIN: {company.gstin}</div>}
          </div>

          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] tracking-widest uppercase text-slate-500">{tabTitle} Tasks</div>
              <div className="font-heading text-lg font-semibold">{rows.length} task(s) · Total {inr(totalAmt)}</div>
            </div>
            <div className="text-[11px] text-slate-500 font-mono">
              Generated {(() => {
                const d = new Date(data.created_at);
                const dd = String(d.getDate()).padStart(2, "0");
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                const hh = String(d.getHours()).padStart(2, "0");
                const mi = String(d.getMinutes()).padStart(2, "0");
                return `${dd}-${mm}-${d.getFullYear()} ${hh}:${mi}`;
              })()}
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="text-center text-slate-500 py-10 text-sm">No entries.</div>
          ) : (
            <table className="w-full text-[12.5px] border-collapse">
              <thead>
                <tr className="bg-slate-100 text-left">
                  <th className="px-3 py-2 text-[10.5px] uppercase tracking-wider text-slate-600">Party</th>
                  <th className="px-3 py-2 text-[10.5px] uppercase tracking-wider text-slate-600">Contact</th>
                  <th className="px-3 py-2 text-[10.5px] uppercase tracking-wider text-slate-600">Items</th>
                  <th className="px-3 py-2 text-[10.5px] uppercase tracking-wider text-slate-600">Deadline</th>
                  <th className="px-3 py-2 text-[10.5px] uppercase tracking-wider text-slate-600 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 align-top">
                    <td className="px-3 py-2.5 font-semibold">{r.customer_name || "—"}</td>
                    <td className="px-3 py-2.5 text-[11.5px] font-mono text-slate-600">
                      {r.customer_phone && <div>{r.customer_phone}</div>}
                      {r.customer_email && <div className="text-[11px]">{r.customer_email}</div>}
                      {!r.customer_phone && !r.customer_email && "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      {(r.lines && r.lines.length > 0) ? (
                        <div className="space-y-0.5">
                          {r.lines.map((ln, i) => (
                            <div key={i}>• {ln.item_name || "—"} <span className="text-slate-500 font-mono text-[11px]">× {ln.quantity || 1}</span></div>
                          ))}
                        </div>
                      ) : (r.item_name || "—")}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11.5px]">{fmtDeadline(r.deadline)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.amount ? inr(r.amount) : "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td colSpan={4} className="px-3 py-3 text-right">Total</td>
                  <td className="px-3 py-3 text-right font-mono">{inr(totalAmt)}</td>
                </tr>
              </tfoot>
            </table>
          )}

          <div className="mt-8 pt-4 border-t border-dashed border-slate-300 text-[11px] text-slate-500 text-center">
            Sent from {company.name || "Vetrich ERP"} · Read-only preview · Not an invoice
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
